import * as React from 'react';
import styled from '@emotion/styled';
import moment, { Moment } from 'moment';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import CalendarInput from 'common/calendar-input/calendar-input';
import { useNavigate } from 'react-router-dom';
import { Popup } from 'semantic-ui-react';
import { IoIosHelpCircle } from 'react-icons/io';

import './dashboard.scss';
import 'react-circular-progressbar/dist/styles.css';
import WithSideBarAndNavBar from 'layouts/withSideBarAndNavBar/withSideBarAndNavBar';
import Briefcase from 'assets/icons/briefcase.png';
import MoneyBag from 'assets/icons/moneybag.png';
import LaptopUse from 'assets/icons/laptopuse.png';
import Button from 'common/button/button';
import Modal from 'common/modal/modal';
import Label from 'common/label/label';

// Assets
import { ReactComponent as LeftArrow } from 'assets/icons/left-arrow.svg';
import { ReactComponent as RightArrow } from 'assets/icons/right-arrow.svg';
import { ReactComponent as Welcome } from 'assets/images/welcome.svg';

// Data
import StandByToJoinData from 'dummy-data/stand-by-to-join.json';
import noticesData from 'dummy-data/notices.json';
import workLogData from 'dummy-data/worklog.json';
import workLog2Data from 'dummy-data/worklog2.json';
import StatModal from 'components/dashboard/stat-modals/stat-modals';
import Carousel from 'components/carousel/carousel';
import DateSelector from 'common/date-selector/date-selector';
import useLoadingStore from 'zustands/useLoadingStore';
import { AxiosResponse } from 'axios';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';
import useStoreStore from 'zustands/useStoreStore';
import { IEmp, IEmpSch, IWorkLogItem } from 'types/dashboard';
import { useEffect, useState } from 'react';
import { getProfileImg } from 'constants/utils';
import useUserStore from 'zustands/useUserStore';

const PopupDescText = styled.div`
  color: white;
  font-size: 0.9rem;
`;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  const { setLoadingState } = useLoadingStore();
  const { userType, setManagerInfo } = useUserStore();

  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [dashboardDate, setDashboardDate] = useState<Moment>(moment()); // 대쉬보드 일자
  const [staffModal, setStaffModal] = useState<boolean>(false); // 전체직원 모달 표시여부
  const [staffData, setStaffData] = useState<IEmpSch[]>([]);

  const [tardyModal, setTardyModal] = useState<boolean>(false); // 지각직원 모달 표시여부
  const [tardyData, setTardyData] = useState<IEmpSch[]>([]);

  const [notAtWorkModal, setNotAtWorkModal] = useState<boolean>(false); // 미출근직원 모달 표시여부
  const [notAtWorkData, setNotAtWorkData] = useState<IEmpSch[]>([]);

  const [workingEmpModal, setWorkingEmpModal] = useState<boolean>(false); // 근무중직원 모달 표시여부
  const [workingData, setWorkingData] = useState<IEmpSch[]>([]);

  const [leaveWorkModal, setLeaveWorkModal] = useState<boolean>(false); // 퇴근직원 모달 표시여부
  const [leaveData, setLeaveData] = useState<IEmpSch[]>([]);

  const [standByToJoinModal, setStandByToJoinModal] = useState<boolean>(false); // 합류대기 모달 표시여부
  const [standByToJoinData, setStandByToJoinData] = useState<IEmpSch[]>([]);

  const [statDate, setStatDate] = useState<Moment>(moment()); // 통계 일자
  const [salaryStatsChart, setSalaryStatsChart] = useState<boolean>(false); // 급여통계 모달 표시여부
  const [absenteeismStatsChart, setabsenteeismStatsChart] = useState<boolean>(false); // 근태통계 모달 표시여부
  const [employeeStatsChart, setEmployeeStatsChart] = useState<boolean>(false); // 직원통계 모달표시여부

  const [checklistData, setChecklistData] = useState<string[]>([]); // 체크리스트
  const [ShelfLifeData, setShelfLifeData] = useState<string[]>([]); // 유통기한
  const [payStubData, setPayStubData] = useState<number[]>([0, 0]); // 월별 급여명세서 전송 횟수 [전송멤버, 총멤버수]

  const [worklog, setWorklog] = useState<IWorkLogItem[]>([]); // 지시사항
  const [worklog2, setWorklog2] = useState<IWorkLogItem[]>([]); // 특이사항
  const [worklogTableToggle, setWorklogTableToggle] = useState<string>('INSTRUCTIONS'); // 지시/특이 보기 토글
  const [notices, setNotices] = useState<Array<any>>([]); // 공지사항
  const [dayMonthYearSelect, setDayMonthYearSelect] = useState<string>('DAY');

  const fetchData = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode(
        'get',
        `/auth/getMainInfo?SELECT_DATE=${moment(dashboardDate).format('YYYY-MM-DD')}`,
      );
      console.log('/auth/getMainInfo', data);

      if (data.resultCode === 0) {
        // 근태 상태
        setStaffData(
          data.resultData.allemp.map((i: any) => ({
            empSeq: i.EMP_SEQ,
            empName: i.EMP_NAME,
            gender: i.GENDER === '1' ? '남자' : '여자',
            isManager: i.IS_MANAGER === 1 ? '매니저' : '스태프',
            workType: i.CALENDAR === '0' ? '고정' : '자율',
          })),
        );
        setTardyData(
          data.resultData.late.map((i: any) => ({
            empSeq: i.EMP_SEQ,
            empName: i.EMP_NAME,
            gender: i.GENDER === '1' ? '남자' : '여자',
            isManager: i.IS_MANAGER === 1 ? '매니저' : '스태프',
            workStartTime: i.NEW_CHANGE_START
              ? moment(i.NEW_CHANGE_START).format('HH:mm')
              : i.NEW_START
              ? moment(i.NEW_START).format('HH:mm')
              : '자율출퇴근',
            realStartTime: i.NEW_UPDATED_START
              ? moment(i.NEW_UPDATED_START).format('HH:mm')
              : i.START_TIME
              ? i.START_TIME.slice(0, 5)
              : '미출근',
          })),
        );
        setNotAtWorkData(
          data.resultData.willwork.map((i: any) => ({
            empSeq: i.EMP_SEQ,
            empName: i.EMP_NAME,
            gender: i.GENDER === '1' ? '남자' : '여자',
            isManager: i.IS_MANAGER === 1 ? '매니저' : '스태프',
            workStartTime: i.NEW_CHANGE_START
              ? moment(i.NEW_CHANGE_START).format('HH:mm')
              : i.NEW_START
              ? moment(i.NEW_START).format('HH:mm')
              : '자율출퇴근',
          })),
        );
        setWorkingData(
          data.resultData.working.map((i: any) => ({
            empSeq: i.EMP_SEQ,
            empName: i.EMP_NAME,
            gender: i.GENDER === '1' ? '남자' : '여자',
            isManager: i.IS_MANAGER === 1 ? '매니저' : '스태프',
            realStartTime: i.NEW_UPDATED_START
              ? moment(i.NEW_UPDATED_START).format('HH:mm')
              : moment(moment().format('YYYY.MM.DD ') + i.START_TIME).format('HH:mm'),
            realEndTime: i.NEW_UPDATED_END
              ? moment(i.NEW_UPDATED_END).format('HH:mm')
              : i.END_TIME
              ? i.END_TIME.slice(0, 5)
              : '미퇴근',
          })),
        );
        setLeaveData(
          data.resultData.workoff.map((i: any) => ({
            empSeq: i.EMP_SEQ,
            empName: i.EMP_NAME,
            gender: i.GENDER === '1' ? '남자' : '여자',
            isManager: i.IS_MANAGER === 1 ? '매니저' : '스태프',
            workEndTime: i.NEW_CHANGE_END
              ? moment(i.NEW_CHANGE_END).format('HH:mm')
              : moment(moment().format('YYYY.MM.DD ') + i.END).format('HH:mm'),
            realEndTime: i.NEW_UPDATED_END ? moment(i.NEW_UPDATED_END).format('HH:mm') : i.END_TIME.slice(0, 5),
          })),
        );
        setStandByToJoinData(
          data.resultData.waiting_emps.map((i: any) => ({
            empSeq: i.EMP_SEQ,
            empName: i.EMP_NAME,
            phone: i.MobileNo,
            invitedDate: moment(i.regdate).format('YYYY.MM.DD'),
          })),
        );
        // 통계
        // 업무관리
        setChecklistData(data.resultData.checklist.map((i: any) => i.CHECK_SEQ));
        setShelfLifeData(data.resultData.shelfLife.map((i: any) => i.checkType));
        setPayStubData([data.resultData.paystub_total.length, data.resultData.paystub_member.length]);

        // 지시,특이사항
        setWorklog(
          [...data.resultData.notice_basic, ...data.resultData.notice_favorite].map((i: any) => ({
            workLogSeq: i.NOTICE_SEQ,
            addDate: moment(i.ADDDATE).format('YYYY.MM.DD HH:mm'),
            empName: i.EMP_NAME,
            gender: i.GENDER === '1' ? '남자' : '여자',
            title: i.TITLE,
          })),
        );
        setWorklog2(
          [...data.resultData.special_notice_basic, ...data.resultData.special_notice_favorite].map((i: any) => ({
            workLogSeq: i.NOTICE_SEQ,
            addDate: moment(i.ADDDATE).format('YYYY.MM.DD HH:mm'),
            empName: i.EMP_NAME,
            gender: i.GENDER === '1' ? '남자' : '여자',
            title: i.TITLE,
          })),
        );
        // 공지사항
        // setNotices(data.resultData.special_notice_basic)

        // 매니저 권한
        if (data.resultData.manager.length > 0) {
          setManagerInfo({
            empSeq: data.resultData.manager[0].EMP_SEQ,
            payShow: data.resultData.manager[0].PAY_SHOW == '1' ? true : false,
            storePayShow: data.resultData.manager[0].STOREPAY_SHOW == '1' ? true : false,
            otherPayShow: data.resultData.manager[0].OTHERPAY_SHOW == '1' ? true : false,
            calendarEdit: data.resultData.manager[0].CalendarEdit == '1' ? true : false,
            recievePush: data.resultData.manager[0].PUSH == '1' ? true : false,
          });
        }
      } else {
        throw new Error('/dashboard fetchData');
      }
    } catch (err) {
      checkErrorLog(err, 'dashboard fetchData');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  useEffect(() => {
    fetchData();
  }, [dashboardDate]);

  const leaveWorkToggleModal = (): any => {
    setLeaveWorkModal(!leaveWorkModal);
  };

  const staffToggleModal = (): any => {
    setStaffModal(!staffModal);
  };

  const tardyToggleModal = (): any => {
    setTardyModal(!tardyModal);
  };

  const notAtWorktoggleModal = (): any => {
    setNotAtWorkModal(!notAtWorkModal);
  };

  const onDutyToggleModal = (): any => {
    setWorkingEmpModal(!workingEmpModal);
  };

  const standByToJoinToggleModal = (): any => {
    setStandByToJoinModal(!standByToJoinModal);
  };

  const handleDateChange = (event: any, arrow?: string) => {
    // Fetch data based on the selected date
    if (arrow === 'left') {
      setDashboardDate(moment(dashboardDate).subtract(1, 'days'));
    } else if (arrow === 'right') {
      setDashboardDate(moment(dashboardDate).add(1, 'days'));
    } else {
      setDashboardDate(moment(event));
    }
  };

  const staffTableHeads = ['이름', '직책', '근무유형', '자세히'];
  const tardyTableHeads = ['이름', '직책', '출근예정', '출근시간', '자세히'];
  const notAtWorktableHeads = ['이름', '직책', '출근예정', '자세히'];
  const workingTableHeads = ['이름', '직책', '출근시간', '퇴근시간', '자세히'];
  const leaveWorkTableHeads = ['이름', '직책', '퇴근예정', '퇴근시간', '자세히'];
  const standByToJoinTableHeads = ['이름', '휴대폰번호', '초대날짜', '자세히'];

  const handleProgressbar = (componentNumber: number) => {
    if (componentNumber === 14) {
      navigate(`/checklist`);
    }
    if (componentNumber === 0) {
      // Route not given
    }
    if (componentNumber === 1) {
      if (userType === '1') {
        navigate(`/salary-specification-transfer-of-mgt`);
      }
    }
  };

  const clickSelectBusinessEstablishment = () => {
    navigate(`/business-establishment-choice`);
  };

  const salaryStatsChartClick = () => {
    setSalaryStatsChart(!salaryStatsChart);
  };

  const absenteeismStatsChartClick = () => {
    setabsenteeismStatsChart(!absenteeismStatsChart);
  };

  const employeeStatsChartClick = () => {
    setEmployeeStatsChart(!employeeStatsChart);
  };

  const toggleWorklogTables = (table: string) => {
    setWorklogTableToggle(table);
  };

  const navigateWorklog = () => {
    navigate(`/work-a-diary`);
  };

  // 상세정보 화면 이동
  const enterDetailInfo = (empData: IEmpSch) => {
    navigate('/view-information', {
      state: {
        empData: {
          ...empData,
          EMP_SEQ: empData.empSeq,
        },
      },
    });
  };

  // 직원초대 화면 이동
  const enterInvite = () => {
    navigate('/employee-registration');
  };

  const dayMonthYearSelectHandle = (type: string) => {
    setDayMonthYearSelect(type);
  };

  const checklistCompleted = checklistData.filter((check) => check).length;
  const checklistCount = checklistData.length;
  const checklistPercent = checklistCount === 0 ? 0 : Math.floor((checklistCompleted / checklistCount) * 100);
  const shelfLifeCompleted = ShelfLifeData.filter((shelf) => shelf === '1').length;
  const shelfLifeCount = ShelfLifeData.length;
  const shelfLifePercent = shelfLifeCount === 0 ? 0 : Math.floor((shelfLifeCompleted / shelfLifeCount) * 100);
  const payStubCompleted = payStubData[0];
  const payStubCount = payStubData[1];
  const payStubPercent = payStubCount === 0 ? 0 : Math.floor((payStubCompleted / payStubCount) * 100);

  return (
    <WithSideBarAndNavBar clickSelectBusinessEstab={clickSelectBusinessEstablishment}>
      <div className="dashboard">
        <div>
          <div className="dashboard__head">
            <div className="dashboard__head-text">근태관리</div>
            <div className="dashboard__head-buttons">
              <button className="arrows__left" onClick={(event) => handleDateChange(event, 'left')}>
                <LeftArrow />
              </button>
              <DateSelector onChange={handleDateChange} value={dashboardDate.toDate()} />
              <button className="arrows__right" onClick={(event) => handleDateChange(event, 'right')}>
                <RightArrow />
              </button>
              {/* <div className="dashboard__button-list">
                <button
                  className={
                    dayMonthYearSelect === 'DAY' ? 'dashboard__head-button__active' : 'dashboard__head-button__inactive'
                  }
                  onClick={() => dayMonthYearSelectHandle('DAY')}>
                  일
                </button>
                <button
                  className={
                    dayMonthYearSelect === 'MONTH'
                      ? 'dashboard__head-button__active'
                      : 'dashboard__head-button__inactive'
                  }
                  onClick={() => dayMonthYearSelectHandle('MONTH')}>
                  월
                </button>
                <button
                  className={
                    dayMonthYearSelect === 'YEAR'
                      ? 'dashboard__head-button__active'
                      : 'dashboard__head-button__inactive'
                  }
                  onClick={() => dayMonthYearSelectHandle('YEAR')}>
                  년
                </button>
              </div> */}
            </div>
          </div>
        </div>
        <div className="dashboard-row">
          <div className="dashboard-row__col1">
            <div>
              <div className="card card__active" onClick={staffToggleModal}>
                <div className="card__card-text">
                  <div className="card__card-text__text">전체</div>
                  <div className="card__card-text__number">{staffData.length}</div>
                </div>
              </div>
              <Modal
                isVisible={staffModal}
                onClose={staffToggleModal}
                headerText="전체 직원"
                subHeaderText="사업장 직원리스트입니다"
                modalType="staff"
                tableHeads={staffTableHeads}
                tableData={staffData}
                enterDetailInfo={enterDetailInfo}
              />
            </div>
            <div>
              {/* Tardy */}
              <div className="card card__inactive" onClick={tardyToggleModal}>
                <div className="card__card-text">
                  <div className="card__card-text__text">지각</div>
                  <div className="card__card-text__number">{tardyData.length}</div>
                </div>
              </div>
              <Modal
                isVisible={tardyModal}
                onClose={tardyToggleModal}
                headerText="지각 직원"
                subHeaderText="오늘 지각한 직원리스트입니다"
                modalType="tardy"
                tableHeads={tardyTableHeads}
                tableData={tardyData}
                enterDetailInfo={enterDetailInfo}
              />
            </div>
            <div>
              <div className="card" onClick={notAtWorktoggleModal}>
                <div className="card__card-text">
                  <div className="card__card-text__text">미출근</div>
                  <div className="card__card-text__number">{notAtWorkData.length}</div>
                </div>
              </div>
              <Modal
                isVisible={notAtWorkModal}
                onClose={notAtWorktoggleModal}
                headerText="미출근 직원"
                subHeaderText="오늘 미출근한 직원리스트입니다"
                modalType="notAtWork"
                tableHeads={notAtWorktableHeads}
                tableData={notAtWorkData}
                enterDetailInfo={enterDetailInfo}
              />
            </div>
            <div>
              <div className="card" onClick={onDutyToggleModal}>
                <div className="card__card-text">
                  <div className="card__card-text__text">근무중</div>
                  <div className="card__card-text__number">{workingData.length}</div>
                </div>
              </div>
              <Modal
                isVisible={workingEmpModal}
                onClose={onDutyToggleModal}
                headerText="근무 직원"
                subHeaderText="현재 근무중인 직원리스트입니다"
                modalType="working"
                tableHeads={workingTableHeads}
                tableData={workingData}
                enterDetailInfo={enterDetailInfo}
              />
            </div>
            <div>
              <div className="card" onClick={leaveWorkToggleModal}>
                <div className="card__card-text">
                  <div className="card__card-text__text">퇴근</div>
                  <div className="card__card-text__number">{leaveData.length}</div>
                </div>
              </div>
              <Modal
                isVisible={leaveWorkModal}
                onClose={leaveWorkToggleModal}
                headerText="퇴근 직원"
                subHeaderText="오늘 퇴근한 직원리스트입니다"
                modalType="leaveWork"
                tableHeads={leaveWorkTableHeads}
                tableData={leaveData}
                enterDetailInfo={enterDetailInfo}
              />
            </div>
            <div>
              <div className="card" onClick={standByToJoinToggleModal}>
                <div className="card__card-text">
                  <div className="card__card-text__text">합류대기</div>
                  <div className="card__card-text__number">{standByToJoinData.length}</div>
                </div>
              </div>
              <Modal
                isVisible={standByToJoinModal}
                onClose={standByToJoinToggleModal}
                headerText="합류대기"
                subHeaderText="현재 합류대기중인 직원리스트입니다"
                modalType="standByToJoin"
                tableHeads={standByToJoinTableHeads}
                tableData={standByToJoinData}
                enterDetailInfo={enterInvite}
              />
            </div>
          </div>
          <div className="dashboard-row__col2">
            <div className="dashboard-stats">
              <div className="dashboard-stats__head">
                <span className="dashboard-stats__head__span">통계로</span>내 사업장을 확인해보세요!
              </div>
              {/* <div className="dashboard-stats__content" onClick={salaryStatsChartClick}>
                <div className="briefcase">
                  <img src={MoneyBag} alt="Briefcase" className="briefcase__image" />
                </div>
                <div className="dashboard-stats__text">
                  <div className="dashboard-stats__text__head">급여통계</div>
                  <div className="dashboard-stats__text__body">
                    급여정산일 {moment(dashboardDate).format('YYYY.MM.DD')}
                  </div>
                </div>
              </div> */}
              <div className="dashboard-stats__content line-top" onClick={absenteeismStatsChartClick}>
                <div className="briefcase">
                  <img src={Briefcase} alt="Briefcase" className="briefcase__image" />
                </div>
                <div className="dashboard-stats__text">
                  <div className="dashboard-stats__text__head">근태통계</div>
                  <div className="dashboard-stats__text__body">지각률/조퇴률/결근률</div>
                </div>
              </div>
              <div className="dashboard-stats__content line-top" onClick={employeeStatsChartClick}>
                <div className="briefcase">
                  <img src={LaptopUse} alt="Briefcase" className="briefcase__image" />
                </div>
                <div className="dashboard-stats__text">
                  <div className="dashboard-stats__text__head">직원통계</div>
                  <div className="dashboard-stats__text__body">직원급여유형,구성원 통계</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="business-management">
          <div className="business-management__head">업무관리</div>
          <div className="business-management__content">
            <div className="business-management__left-col" onClick={() => handleProgressbar(14)}>
              <div className="pie-chart__text">
                <strong className="pie-chart__text__head">
                  체크리스트
                  <Popup
                    flowing={true}
                    trigger={<IoIosHelpCircle size={22} color="#d9d9d9" />}
                    position="top center"
                    inverted
                    style={{ borderRadius: 11 }}
                    content="당일 처리된 체크리스트를 보여줍니다."
                  />
                </strong>
                <div className="pie-chart__text__numerator">
                  {checklistCompleted}
                  <span className="pie-chart__text__denominator">/{checklistCount}</span>
                </div>
              </div>
              <div className="pie-chart-media">
                <div className="pie-chart-media__chart">
                  <CircularProgressbarWithChildren
                    value={checklistPercent}
                    strokeWidth={15}
                    styles={buildStyles({
                      pathColor: '#49AE9E',
                      trailColor: '#bee2dc',
                    })}>
                    <div className="pie-chart-media__value pie-chart-media__text-color1">
                      {checklistPercent}
                      <span className="pie-chart-media__percentage">%</span>
                    </div>
                  </CircularProgressbarWithChildren>
                </div>
              </div>
            </div>
            <div
              className="business-management__center-col"
              onClick={() => handleProgressbar(0)}
              style={{ cursor: 'default' }}>
              <div className="pie-chart__text">
                <strong className="pie-chart__text__head">
                  유통기한
                  <Popup
                    flowing={true}
                    trigger={<IoIosHelpCircle size={22} color="#d9d9d9" />}
                    position="top center"
                    inverted
                    style={{ borderRadius: 11 }}
                    content="당일 처리 및 폐기 완료된 유통기한 상품을 보여줍니다."
                  />
                </strong>
                <div className="pie-chart__text__numerator">
                  {shelfLifeCompleted}
                  <span className="pie-chart__text__denominator">/{shelfLifeCount}</span>
                </div>
              </div>
              <div className="pie-chart-media">
                <div className="pie-chart-media__chart">
                  <CircularProgressbarWithChildren
                    value={shelfLifePercent}
                    strokeWidth={15}
                    styles={buildStyles({
                      pathColor: '#fd966c',
                      trailColor: '#fedfd3',
                    })}>
                    <div className="pie-chart-media__value pie-chart-media__text-color2">
                      {shelfLifePercent}
                      <span className="pie-chart-media__percentage">%</span>
                    </div>
                  </CircularProgressbarWithChildren>
                </div>
              </div>
            </div>
            <div
              className="business-management__right-col"
              onClick={() => handleProgressbar(1)}
              style={{ cursor: userType === '1' ? 'pointer' : 'default' }}>
              <div className="pie-chart__text">
                <strong className="pie-chart__text__head">
                  월별 급여명세서 전송
                  <Popup
                    flowing={true}
                    trigger={<IoIosHelpCircle size={22} color="#d9d9d9" />}
                    position="top center"
                    inverted
                    style={{ borderRadius: 11 }}
                    content="당월 발송 완료된 급여명세서를 보여줍니다."
                  />
                </strong>
                <div className="pie-chart__text__numerator">
                  {payStubCompleted}
                  <span className="pie-chart__text__denominator">/{payStubCount}</span>
                </div>
              </div>
              <div className="pie-chart-media">
                <div className="pie-chart-media__chart">
                  <CircularProgressbarWithChildren
                    value={payStubPercent}
                    strokeWidth={15}
                    styles={buildStyles({
                      pathColor: '#fed159',
                      trailColor: '#fff6de',
                    })}>
                    <div className="pie-chart-media__value pie-chart-media__text-color3">
                      {payStubPercent}
                      <span className="pie-chart-media__percentage">%</span>
                    </div>
                  </CircularProgressbarWithChildren>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-row3">
          <div className="dashboard-row3__col1">
            <div className="worklog__table-head">
              <div
                className={'worklog__table-head__instrictions-active'}
                onClick={() => toggleWorklogTables('INSTRUCTIONS')}>
                <div
                  className={
                    worklogTableToggle === 'INSTRUCTIONS'
                      ? 'worklog__table-head__instrictions-active__text'
                      : 'worklog__table-head__instrictions-inactive__text'
                  }>
                  지시사항
                </div>
                <Label
                  text={worklog.length.toString()}
                  type="circular"
                  color_type={worklogTableToggle === 'INSTRUCTIONS' ? 'color1' : 'color2'}
                />
              </div>
              <div
                className="worklog__table-head__special-notes-active"
                onClick={() => toggleWorklogTables('SPECIAL_NOTES')}>
                <div
                  className={
                    worklogTableToggle === 'SPECIAL_NOTES'
                      ? 'worklog__table-head__special-notes-active__text'
                      : 'worklog__table-head__special-notes-inactive__text'
                  }>
                  특이사항
                </div>
                <Label
                  text={worklog2.length.toString()}
                  type="circular"
                  color_type={worklogTableToggle === 'SPECIAL_NOTES' ? 'color1' : 'color2'}
                />
              </div>
              <div className="worklog__table-head__view" onClick={navigateWorklog}>
                전체보기 {'>'}
              </div>
            </div>
            <div className="worklog">
              {worklogTableToggle === 'INSTRUCTIONS' &&
                worklog.length > 0 &&
                worklog.slice(0, 4).map((log) => {
                  return (
                    <div key={log.workLogSeq} className="worklog__table-row">
                      <div className="worklog__worklog-item">
                        <div className="worklog__journal">{log.title}</div>
                        <div className="worklog__item">
                          <div className="worklog__item-top">
                            <div>
                              <img className="worklog__profileimage" src={getProfileImg(log.gender)} alt="profImage" />
                            </div>
                            <div className="worklog__worklog-item__staff">{log.empName}</div>
                            <div className="worklog__worklog-item__date">{log.addDate}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              {worklogTableToggle === 'INSTRUCTIONS' && worklog.length === 0 && (
                <div className="worklog__empty-worklog">
                  <span className="worklog__empty-worklog__text">오늘의 지시사항이 없습니다.</span>
                </div>
              )}
              {worklogTableToggle === 'SPECIAL_NOTES' &&
                worklog2.length > 0 &&
                worklog2.slice(0, 4).map((log, item) => (
                  <div key={log.workLogSeq} className="worklog__table-row">
                    <div className="worklog__worklog-item">
                      <div className="worklog__journal">{log.title}</div>
                      <div className="worklog__item">
                        <div className="worklog__item-top">
                          <div>
                            <img className="worklog__profileimage" src={getProfileImg(log.gender)} alt="profImage" />
                          </div>
                          <div className="worklog__worklog-item__staff">{log.empName}</div>
                          <div className="worklog__worklog-item__date">{log.addDate}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              {worklogTableToggle === 'SPECIAL_NOTES' && worklog2.length === 0 && (
                <div className="worklog__empty-worklog">
                  <div className="worklog__empty-worklog__text">오늘의 특이사항이 없습니다.</div>
                </div>
              )}
            </div>
          </div>
          <div className="dashboard-row3__col2">
            <div className="notice__table-head">
              <div className="notice__table-head__instrictions">
                <div className="notice__table-head__instrictions__text">공지사항</div>
              </div>
            </div>
            <div className="notice">
              {notices.length > 0 ? (
                notices.map((notice, item) => (
                  <div key={notice} className="notice__table-row">
                    <div className={notices.length - 1 != item ? 'notice__worklog-item' : 'notice__worklog-item-last'}>
                      <span className="notice__journal">{notice.text}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="worklog__empty-worklog">
                  <span className="worklog__empty-worklog__text">준비 중 입니다</span>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* <div className="carousel-section">
          <Carousel />
        </div> */}
      </div>
      <StatModal isVisible={salaryStatsChart} onClose={salaryStatsChartClick} modalType={'salaryStats'} />
      <StatModal isVisible={absenteeismStatsChart} onClose={setabsenteeismStatsChart} modalType={'absenteeismStats'} />
      <StatModal isVisible={employeeStatsChart} onClose={employeeStatsChartClick} modalType={'employeeStats'} />
    </WithSideBarAndNavBar>
  );
};
export default Dashboard;
